import { Directive } from "@angular/core";
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { combineLatest, map, Observable, startWith, Subject, takeUntil } from "rxjs";
import { FormObjectComponent } from "../../shared/forms/form-object.component";
import { PartComponent } from "../reference/models/part-component";
import { ReferenceCache } from "../reference/services/reference.cache";
import { ClaimsAppSubmissionConstant } from "./models/claims-app-submission.constant";
import { ComponentGroupService } from "./services/form-service/component-group.service";
import { PartComponentOption } from "./models/part-component-option";

@Directive()
export abstract class ComponentSelector extends FormObjectComponent {
  protected readonly _destroying$ = new Subject<void>();
  
  public isReadOnly: boolean = false;

  public filteredOptions: Observable<PartComponent[]>;
  public componentOptions: PartComponent[] = [];

  /**
   * Key of component group in componentGroupService.
  */
  public componentGroupKey: number;
  public componentFormControl: FormControl<PartComponent>;
  public componentGroup: Observable<string>;

  /**
   * Text input to match on group name as well.
   */
  public shouldMatchFilterOnGroupName: boolean = true;
  public allowCustomPart: boolean = false;

  constructor(protected referenceCache: ReferenceCache,
              protected componentGroupService: ComponentGroupService) {
      super();
  }
  
  ngOnInit(): void {
    this.componentFormControl = new FormControl<PartComponent>({ value: null, disabled: false },
                                                               [this.requireSelectionValidator()]);
                                                               
    this.referenceCache.componentsSubject
        .pipe(takeUntil(this._destroying$))
        .subscribe(result => {
          this.componentOptions = result;
          
          // Filter options based on concern group and input text value.
          // When either the text or the group name changes, update the list.
          this.filteredOptions = combineLatest([
            this.componentFormControl?.valueChanges.pipe(takeUntil(this._destroying$),
                                                         startWith(this.componentFormControl?.value ?? '')),
            this.componentGroupService.getSubject(this.componentGroupKey)
                                      .pipe(takeUntil(this._destroying$),
                                            startWith(this.componentGroupService.getName(this.componentGroupKey) ?? ''))
          ]).pipe(takeUntil(this._destroying$),
                  map(([inputValue, groupName]) => {
                    return ComponentSelector.filterComponentOptionsByText(
                        ComponentSelector.filterComponentOptionsByGroup(this.componentOptions, 
                                                                        groupName ?? ''),
                        inputValue,
                        this.shouldMatchFilterOnGroupName,
                        this.allowCustomPart);
                  })
          );
    });
  }
  
  ngOnDestroy(): void {    
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  /**
   * What to display in the input field when an option is selected.
   * @param option 
   * @returns 
   */ 
  public displayPartOption(option: PartComponent): string {
    return option ? option.name : '';
  }

  /**
   * Get a component by its name and group name from collection.
   * @param name 
   * @param groupName 
   * @returns 
   */
  protected getComponent(name: string, groupName: string): PartComponent {
    return this.componentOptions.find(c => c.name == name && c.groupName == groupName);
  }
  
  /**
   * Filter Component options based on the group name.
   * @param options 
   * @param groupname 
   * @returns 
   */
  private static filterComponentOptionsByGroup(options: PartComponent[],
                                               groupname: string) : PartComponent[] {
    if (!groupname || groupname.trim() === '') {
      return options;
    }
  
    return options.filter(option => option.groupName.trim().toLowerCase() == groupname.trim().toLowerCase());
  }
  
  /**
   * Filter Component options based on the value entered in the input field.
   * Adds a custom part selection if allowCustomPart is true.
   * @param options 
   * @param inputText 
   * @param allowCustomPart 
   * @returns 
   */
  private static filterComponentOptionsByText(options: PartComponent[],
                                              inputText: string | PartComponent,
                                              shouldMatchFilterOnGroupName: boolean,
                                              allowCustomPart: boolean): PartComponent[] {
    let filterValue: string = '';
  
    // Determine if the value is a string or an object
    if (typeof inputText === 'string') {
      filterValue = inputText;  // Don't trim here, allow multi-word input
    } else {
      filterValue = inputText?.name ?? '';
    }  
    
    // Convert options to PartComponentOption
    const searchOptions = options.map(option => new PartComponentOption(option));   
    
    // Filter options based on the input value
    // 1. Component name includes input value.
    // 2. If shouldMatchFilterOnGroupName is true
    //    2a. Component group name includes input value.
    //    2b. searchText includes input value. This could differ from the UI.
    let results = searchOptions.filter(option => option.name.toLowerCase().includes(filterValue.toLowerCase())
                                                 || (shouldMatchFilterOnGroupName
                                                     && (option.groupName.toLowerCase().includes(filterValue.toLowerCase())
                                                         || option.searchText.toLowerCase().includes(filterValue.toLowerCase()))));
    
    // If value is empty string, return all options
    if (filterValue.trim() == '') {
      results = searchOptions;
    }
  
    // If custom parts is allowed and no results found, add a custom option for creating a new component
    if (allowCustomPart && results.length === 0) {
      // Create custom option with original input (will be trimmed when selected)
      results.push(new PartComponentOption({ 
        name: filterValue, 
        groupName: ClaimsAppSubmissionConstant.createNewComponentLabel, 
        code: '' 
      }));
    }
  
    return results;
  }
  
  /**
   * Validator to ensure that a selection has been made from the component options.
   * If the value is an object (selected from the list), it is valid.
   * If the value is a string (not selected), it is invalid.
   */
  public requireSelectionValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (typeof control.value === 'object' && control.value !== null) {
        return null;
      }
      return { requiredSelection: true };
    };
  }
}
